import { SelectNameValue } from 'business/models/CustomModels';
import {
  ProteinFormulationIngredientTypeDTO,
  ProteinFormulationVariantDTO,
  ProteinFormulationVariantIngredientDTO,
} from 'business/models/Models';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import CheckBox from 'components/customComponents/Experiment/ImagingSchedule/CheckBox';
import { getRandomNegativeNumber } from 'components/main/Setup/Imaging/utils';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { navigationBlockWarningMessage, showAlertSnackBar } from 'utils/Global';
import { RMInput } from '../RMInput';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';

export const useGetHeaderButtons = (
  setShowModal,
  onSave,
  isDirty,
  onDiscardButtonClick,
  isError,
) => {
  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: isDirty ? (isError ? false : true) : false,
    onSave: () => {
      onSave();
    },
    onDiscard: () => {
      onDiscardButtonClick();
    },
    saveButtonText: 'Save',
    discardButtonText: 'Cancel',
    alwaysEnableDiscard: true,
    navigationBlockCallback: () => {
      if (isDirty) {
        return navigationBlockWarningMessage;
      }
    },
  };

  const ingredientSaveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);

  return ingredientSaveDiscardButtons;
};

export const useProteinFormulationData = (parentProteinTreeNodeId) => {
  const [pfData, setPfData] = useState(null);
  const [pfIngredientTypeOptions, setPfIngredientTypeOptions] = useState<
    SelectNameValue[]
  >([]);
  const [pfIngredientTypes, setPfIngredientTypes] = useState<
    ProteinFormulationIngredientTypeDTO[]
  >([]);

  useEffect(() => {
    const pfService = new ProteinFormulationService();

    const fetchPfData = async () => {
      try {
        const data = await pfService.getProteinFormulationIngredientsByTreeNodeId(
          parentProteinTreeNodeId,
        );
        setPfData(data);
      } catch (error) {
        console.error('Error fetching pf data:', error);
      }
    };
    fetchPfData();

    const fetchPfIngredientTypes = async () => {
      try {
        const data = await pfService.getProteinFormulationIngredientTypes();
        const pfIngTypesOptions = data.map((item) => ({
          name: item.name,
          value: item.id,
        }));
        setPfIngredientTypeOptions(pfIngTypesOptions);
        setPfIngredientTypes(data);
      } catch (error) {
        console.error('Error fetching pf ingredient types:', error);
      }
    };

    fetchPfIngredientTypes();
  }, []);

  return { pfData, pfIngredientTypeOptions, pfIngredientTypes };
};

export const useCreateProteinFormulationVariants = (
  proteinVariantIngredients,
  pfData,
  validatePFData,
  setShowModal,
  setRefreshVariantList,
  refreshVariantList,
) => {
  const createProteinFormulationVariants = useCallback(async () => {
    if (!validatePFData()) {
      return;
    }

    const pfVariantDTO = new ProteinFormulationVariantDTO();
    pfVariantDTO.deletedProteinFormulationVariantIngredients = [];
    pfVariantDTO.proteinFormulationVariantIngredients = proteinVariantIngredients;
    pfVariantDTO.parentProteinFormulationTreeNodeId = pfData.treeNodeId;
    pfVariantDTO.parentProteinFormulationId = pfData.id;

    const pfService = new ProteinFormulationService();
    try {
      const response = await pfService.createProteinFormulationVariants(pfVariantDTO);
      if (response) {
        setRefreshVariantList(!refreshVariantList);
        setShowModal(false);
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'Protein Variants successfully created',
        );
      } else {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Protein variants cannot be created.',
        );
      }
    } catch (error) {
      console.error('Error creating protein formulation variants:', error);
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'An error occurred while creating protein variants.',
      );
    }
  }, [proteinVariantIngredients, pfData]);

  return { createProteinFormulationVariants };
};

export const useAddVariantIngredient = (
  pfIngredientTypeOptions,
  proteinVariantIngredients,
  setProteinVariantIngredients,
  isError,
  setSelectedIds,
  setIsDirty,
  setEditId,
  setIsSelectAllChecked,
) => {
  const onAddVariantIngredient = () => {
    const rndNum = getRandomNegativeNumber();
    const newData = [...proteinVariantIngredients];

    const newDataItem = new ProteinFormulationVariantIngredientDTO({
      id: rndNum,
      type: pfIngredientTypeOptions[0]?.value || '', // Default to an empty string if no options
      userId: '',
      lot: '',
      name: '',
      molecularWeight: undefined,
      concentrationmgmL: undefined,
      concentrationmM: undefined,
      concentrationPercent: undefined,
      pH: undefined,
    });

    newData.push(newDataItem);
    setProteinVariantIngredients(newData);
    setSelectedIds([newData.length - 1]);
    setIsDirty(true);
    if (!isError) {
      setEditId(rndNum);
    }
    setIsSelectAllChecked(false);
  };

  return { onAddVariantIngredient };
};

export const useDeleteVariantIngredient = (
  proteinVariantIngredients,
  setProteinVariantIngredients,
  selectedProteinIngs,
  setSelectedProteinIngs,
  setIsSelectAllChecked,
) => {
  const onDeleteVariantIngredient = () => {
    selectedProteinIngs.forEach((selectedIng) => {
      const idx = proteinVariantIngredients.findIndex(
        (pfIng) => pfIng.id === selectedIng.id,
      );
      proteinVariantIngredients.splice(idx, 1);
    });
    setProteinVariantIngredients([...proteinVariantIngredients]);
    setSelectedProteinIngs([]);
    setIsSelectAllChecked(false);
  };

  return { onDeleteVariantIngredient };
};

export const useTableColumns = (onChangeHeaderCheckbox, isSelectAllChecked) => {
  const handleTableFilter = (filterable: boolean, filterTitle: string) => (event: any) =>
    (
      <TableSortInputFilter
        event={event}
        title={filterTitle}
        showSearchInput={filterable}
      />
    );

  const columns = [
    {
      Header: (
        <CheckBox
          isChecked={isSelectAllChecked}
          onChange={(e) => {
            onChangeHeaderCheckbox(e);
          }}
        />
      ),
      accessor: 'mainCheck',
      width: '8%',
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: '17%',
      Filter: handleTableFilter(false, 'Type'),
    },
    {
      Header: 'Name',
      accessor: 'name',
      width: '25%',
      Filter: handleTableFilter(false, 'Name'),
    },
    {
      Header: 'ID',
      accessor: 'userId',
      width: '10%',
      Filter: handleTableFilter(false, 'ID'),
    },
    {
      Header: 'Lot',
      accessor: 'lot',
      width: '10%',
      Filter: handleTableFilter(false, 'Lot'),
    },
    {
      Header: 'MW(kDa)',
      accessor: 'molecularWeight',
      width: '10%',
      Filter: handleTableFilter(false, 'MW(kDa)'),
    },
    {
      Header: 'Conc.(mg/mL)',
      accessor: 'concentrationmgmL',
      width: '10%',
      Filter: handleTableFilter(false, 'Conc.(mg/mL)'),
    },
    {
      Header: 'Conc.(mM)',
      accessor: 'concentrationmM',
      width: '10%',
      Filter: handleTableFilter(false, 'Conc.(mM)'),
    },
  ];

  return columns;
};

export const useTableData = (
  proteinVariantIngredients,
  isSelected,
  onCellChange,
  onFieldFocus,
  pfIngredientTypeOptions,
  nameErrors,
  selectedProteinIngs,
) => {
  const tableData = useMemo(() => {
    if (proteinVariantIngredients) {
      return proteinVariantIngredients?.map((ing, _idx) => ({
        index: _idx,
        id: ing.id,
        mainCheck: (
          <CheckBox
            isChecked={isSelected(ing.id)}
            onChange={(e) => onCellChange(e, ing.id, 'mainCheck')}
          />
        ),
        type: (
          <select
            id="proteinvariant-select-table-type"
            className="table-input-protein"
            style={{ width: '100%', padding: '0px', margin: '0px' }}
            value={ing.type}
            onChange={(e) => onCellChange(e, ing.id, 'type')}
            onFocus={(e) => onFieldFocus(e, ing.id, 'type')}
            disabled={false}
          >
            {pfIngredientTypeOptions.map((val) => (
              <option
                id={`proteinvariant-select-table-${val.name}`}
                key={val.value}
                value={val.value}
              >
                {val.name}
              </option>
            ))}
          </select>
        ),
        name:
          ing.name == '' ? (
            <>
              <input
                id={`proteinvariant-table-name-${ing.id}`}
                type="text"
                className="table-input-protein"
                onChange={(e) => onCellChange(e, ing.id, 'name')}
                value={ing.name}
              />
            </>
          ) : (
            <input
              id={`proteinvariant-table-name-${ing.id}-fieldsfocus`}
              type="text"
              className="table-input-protein"
              onChange={(e) => onCellChange(e, ing.id, 'name')}
              onFocus={(e) => onFieldFocus(e, ing.id, 'name')}
              value={ing.name}
            />
          ),
        userId: (
          <input
            id={`proteinvariant-table-userid-${ing.id}`}
            type="text"
            className="table-input-protein"
            onChange={(e) => onCellChange(e, ing.id, 'userId')}
            onFocus={(e) => onFieldFocus(e, ing.id, 'userId')}
            value={ing.userId}
          />
        ),
        lot: (
          <>
            <input
              id={`proteinvariant-table-lot-${ing.id}`}
              type="number"
              className="table-input-protein"
              onChange={(e) => onCellChange(e, ing.id, 'lot')}
              onFocus={(e) => onFieldFocus(e, ing.id, 'lot')}
              value={ing.lot}
            />
            {nameErrors.some((ingId) => ingId == ing.id) && (
              <>
                <div
                  id={`proteinvariant-table-lot-error-${ing.id}`}
                  className="rounded-[0.308rem] mt-[4rem] absolute h-[1.692rem] w-[18%] bg-[#474747] text-[#FFFFFF] text-[0.8rem] place-content-center text-center"
                >
                  Name must be unique, please change it
                </div>
              </>
            )}
          </>
        ),
        molecularWeight: (
          <RMInput
            className="table-input-protein"
            onChange={(e) => onCellChange(e, ing.id, 'molecularWeight')}
            onFocus={(e) => onFieldFocus(e, ing.id, 'molecularWeight')}
            value={ing.molecularWeight}
            allowDecimals
          />
        ),
        concentrationmgmL: (
          <RMInput
            className="table-input-protein"
            onChange={(e) => onCellChange(e, ing.id, 'concentrationmgmL')}
            onFocus={(e) => onFieldFocus(e, ing.id, 'concentrationmgmL')}
            value={ing.concentrationmgmL}
            allowDecimals
          />
        ),
        concentrationmM: (
          <RMInput
            readonly={ing.concentrationmgmL && ing.molecularWeight ? true : false}
            className="table-input-protein"
            onChange={(e) => onCellChange(e, ing.id, 'concentrationmM')}
            onFocus={(e) => onFieldFocus(e, ing.id, 'concentrationmM')}
            value={ing.concentrationmM}
            allowDecimals
          />
        ),
      }));
    }
    return [];
  }, [proteinVariantIngredients, selectedProteinIngs]);

  return tableData;
};
